import { useState, useCallback, useRef, useEffect } from 'react'
import { apiFetch } from '@/lib/api'

export interface UseApiFetchResult<T> {
  data: T | null
  error: string | null
  isLoading: boolean
  refetch: () => void
  mutate: (data: T) => void
}

interface UseApiFetchOptions {
  revalidateOnFocus?: boolean
}

export function useApiFetch<T = unknown>(path: string | null, options?: UseApiFetchOptions): UseApiFetchResult<T> {
  const [state, setState] = useState<{ data: T | null; error: string | null; isLoading: boolean }>({
    data: null, error: null, isLoading: !!path,
  })
  const pathRef = useRef(path)

  const fetchData = useCallback(async () => {
    const currentPath = pathRef.current
    if (!currentPath) return
    // 기존 데이터가 없을 때만 isLoading 표시 (초기 로딩)
    // refetch 시에는 기존 데이터를 유지하며 백그라운드 갱신 (SWR 패턴)
    setState(s => s.data
      ? { ...s, error: null }
      : { ...s, isLoading: true, error: null }
    )
    try {
      const data = await apiFetch<T>(currentPath)
      setState({ data, error: null, isLoading: false })
    } catch (e: any) {
      // refetch 실패 시에도 기존 data 유지
      setState(s => ({ data: s.data, error: e.message, isLoading: false }))
    }
  }, [])

  useEffect(() => {
    pathRef.current = path
    if (path) fetchData()
    else setState({ data: null, error: null, isLoading: false })
  }, [path, fetchData])

  // 탭 포커스 시 자동 refetch
  useEffect(() => {
    if (!options?.revalidateOnFocus) return
    const onFocus = () => fetchData()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [options?.revalidateOnFocus, fetchData])

  return {
    ...state,
    refetch: fetchData,
    mutate: (data: T) => setState(s => ({ ...s, data })),
  }
}
